// The Graphs tab's gate for a clear, a delete and a clear of every managed
// graph. A clear or a delete names the graph and asks yes or no; clearing
// every managed graph at once is the store-wide operation, and only it asks
// for a typed phrase (Ch08 §2.5).

import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { fieldClass } from '@/components/ui/field';
import { type GraphInfo, graphDisplayLabel } from '@/lib/useGraphs';

export type GraphOp = 'clear' | 'delete' | 'clear-all';

/** What the user types to confirm clearing every managed graph at once. */
export const CLEAR_ALL_PHRASE = 'all managed graphs';

const TITLE: Record<GraphOp, string> = {
  clear: 'Clear graph',
  'clear-all': 'Clear all managed graphs',
  delete: 'Delete graph',
};

export function ConfirmGraphOpModal({
  op,
  graph,
  managed,
  onCancel,
  onConfirm,
}: {
  op: GraphOp;
  /** The graph a clear or a delete acts on; none for clear-all. */
  graph: GraphInfo | null;
  managed: GraphInfo[];
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const name = graph ? graphDisplayLabel(graph) : '';
  const ready = op !== 'clear-all' || typed.trim() === CLEAR_ALL_PHRASE;
  const action = op === 'clear-all' ? TITLE[op] : `${op === 'clear' ? 'Clear' : 'Delete'} ${name}`;
  return (
    <Modal
      title={TITLE[op]}
      danger
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="dangerFilled" size="lg" onClick={onConfirm} disabled={!ready}>
            {action}
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted-foreground">
        {op === 'clear-all' ? (
          <>
            Every statement in the managed graphs will be removed in one operation, followed by one
            index rebuild. The graphs themselves remain. This cannot be undone.
          </>
        ) : (
          <>
            {op === 'clear' ? 'Every statement in ' : ''}
            <strong className="text-tres-text-primary">{name}</strong>{' '}
            <span className="font-mono text-xs">({graph?.bare})</span>
            {op === 'clear'
              ? ' will be removed. The graph itself, and any role it has, remains. This cannot be undone.'
              : ' will be removed entirely, with all its statements and its role. This cannot be undone.'}
          </>
        )}
      </p>
      {op === 'clear-all' && (
        <>
          <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
            {managed.map((g) => (
              <li key={g.bare} className="flex justify-between gap-4">
                <span>{graphDisplayLabel(g)}</span>
                <span className="font-mono">
                  {g.statements === undefined ? '—' : g.statements.toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
          <label className="mt-4 block">
            <span className="text-xs text-muted-foreground">
              Type <strong>{CLEAR_ALL_PHRASE}</strong> to confirm:
            </span>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              spellCheck={false}
              autoFocus
              placeholder={CLEAR_ALL_PHRASE}
              className={fieldClass('normal', 'mt-1 block w-full')}
            />
          </label>
        </>
      )}
    </Modal>
  );
}
